import { useCallback, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import type { DeleteSearchItem } from '../api/deletes'
import { importApi } from '../api/imports'
import type { EntityDef } from '../api/types'
import { ErrorState, LoadingState } from '../components/States'
import { useAsync } from '../hooks/useAsync'
import { DATE_RANGES, DeleteWizardContext } from './DeleteWizardContext'
import type { DeleteSource, DeleteWizard } from './DeleteWizardContext'

/**
 * Holds the delete wizard's choices between steps. Nothing is persisted here;
 * the final step submits them to `POST /api/delete/submit`, which queues a job
 * that deletes the matching records from QuickBooks.
 *
 * The entity list comes from `GET /api/import/entities` — there is no delete
 * entities endpoint, and that one already carries the `isTransaction` flag the
 * Lists / Transactions toggle needs. Same trade-off as the export wizard.
 */
export function DeleteWizardProvider() {
  const entities = useAsync<EntityDef[]>(() => importApi.entities(), [])
  const [entityType, setEntityTypeState] = useState('')
  const [source, setSource] = useState<DeleteSource>('filter')
  const [dateRange, setDateRange] = useState(DATE_RANGES[0])
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [amountMin, setAmountMin] = useState('')
  const [amountMax, setAmountMax] = useState('')
  const [refs, setRefs] = useState('')
  const [review, setReview] = useState<DeleteSearchItem[] | null>(null)
  const [exportColumns, setExportColumns] = useState<string[] | null>(null)

  // Changing the subject invalidates everything scoped to it.
  const setEntityType = useCallback((name: string) => {
    setEntityTypeState(name)
    setRefs('')
    setAmountMin('')
    setAmountMax('')
    setReview(null)
    setExportColumns(null)
  }, [])

  const reset = useCallback(() => {
    setEntityTypeState('')
    setSource('filter')
    setDateRange(DATE_RANGES[0])
    setFrom('')
    setTo('')
    setAmountMin('')
    setAmountMax('')
    setRefs('')
    setReview(null)
    setExportColumns(null)
  }, [])

  const list = useMemo(() => entities.data ?? [], [entities.data])
  const refList = useMemo(
    () =>
      refs
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
    [refs],
  )

  const value = useMemo<DeleteWizard>(
    () => ({
      entities: list,
      entityType,
      entity: list.find((e) => e.name === entityType) ?? null,
      source,
      dateRange,
      from,
      to,
      amountMin,
      amountMax,
      refs,
      refList,
      review,
      exportColumns,
      setReview,
      setExportColumns,
      setEntityType,
      setSource,
      setDateRange,
      setFrom,
      setTo,
      setAmountMin,
      setAmountMax,
      setRefs,
      reset,
    }),
    [
      list, entityType, source, dateRange, from, to, amountMin, amountMax, refs,
      refList, review, exportColumns, setEntityType, reset,
    ],
  )

  if (entities.loading) return <LoadingState label="Loading record types…" />
  if (entities.error) return <ErrorState error={entities.error} onRetry={entities.reload} />

  return (
    <DeleteWizardContext.Provider value={value}>
      <Outlet />
    </DeleteWizardContext.Provider>
  )
}
